import * as React from "react"

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { cva } from "class-variance-authority"
import { Check, ChevronRight } from "lucide-react"

import { cn } from "../../cn"
import { SearchInput, type SearchInputProps } from "../SearchInput/SearchInput"

const dropdownMenuContentVariants = cva([
  "z-50 min-w-[8rem] rounded-md",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
  "p-1",
])

const dropdownMenuItemVariants = cva([
  "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 px-2",
  "text-sm outline-none",
  "transition-colors duration-150",
  "data-[disabled]:cursor-default data-[disabled]:opacity-50",
])

/* ── Root ──────────────────────────────────────────────────── */

const DropdownMenu = DropdownMenuPrimitive.Root
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
const DropdownMenuGroup = DropdownMenuPrimitive.Group

/* ── Content ───────────────────────────────────────────────── */

const DropdownMenuContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(dropdownMenuContentVariants(), className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))

DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

/* ── Item ──────────────────────────────────────────────────── */

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> {
  destructive?: boolean
}

const DropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, destructive, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      dropdownMenuItemVariants(),
      destructive
        ? "text-coval-danger-text focus:bg-coval-danger-surface focus:text-coval-danger-on-surface [--coval-focus-ring:currentColor]"
        : "text-coval-text focus:bg-surface-contrast",
      className,
    )}
    {...props}
  />
))

DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

/* ── Checkbox item ─────────────────────────────────────────── */

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    checked={checked}
    className={cn(
      dropdownMenuItemVariants(),
      "gap-2 pl-2 text-coval-text focus:bg-surface-contrast",
      className,
    )}
    {...props}
  >
    <span className="flex size-4 shrink-0 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="size-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))

DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

/* ── Label ─────────────────────────────────────────────────── */

const DropdownMenuLabel = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  />
))

DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

/* ── Separator ─────────────────────────────────────────────── */

const DropdownMenuSeparator = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-surface-1-hover", className)}
    {...props}
  />
))

DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

/* ── Search ────────────────────────────────────────────────── */

/**
 * A filter field for the top of a long menu. It takes focus once the menu
 * opens, keeps typed characters out of the menu's typeahead, and hands focus
 * to the first enabled item on ArrowDown. Filtering the items is left to the
 * caller, which owns the list.
 */
const DropdownMenuSearch = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, onKeyDown, onClear, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null)

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof forwardedRef === "function") {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      },
      [forwardedRef],
    )

    // The field claims focus each time its menu opens, including a
    // force-mounted menu that stays mounted between opens, and never while the
    // menu is closed. The menu focuses its own content as it opens, so the
    // field takes focus on the next frame.
    React.useEffect(() => {
      const menu = innerRef.current?.closest<HTMLElement>("[role=menu]")
      if (!menu) {
        return
      }
      let frame = 0
      const focusIfOpen = () => {
        cancelAnimationFrame(frame)
        if (menu.dataset.state === "open") {
          frame = requestAnimationFrame(() => innerRef.current?.focus())
        }
      }
      focusIfOpen()
      const observer = new MutationObserver(focusIfOpen)
      observer.observe(menu, {
        attributes: true,
        attributeFilter: ["data-state"],
      })
      return () => {
        observer.disconnect()
        cancelAnimationFrame(frame)
      }
    }, [])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event)
      if (event.key === "ArrowDown") {
        event.preventDefault()
        event.currentTarget
          .closest<HTMLElement>("[role=menu]")
          ?.querySelector<HTMLElement>(
            ":is([role=menuitem], [role=menuitemcheckbox], [role=menuitemradio]):not([data-disabled])",
          )
          ?.focus()
      }
      if (event.key !== "Escape") {
        event.stopPropagation()
      }
    }

    // Clearing unmounts the focused clear button, so focus returns to the
    // field to let the user keep typing.
    const handleClear = () => {
      onClear()
      innerRef.current?.focus()
    }

    return (
      <div className="border-b border-coval-border p-2">
        <SearchInput
          ref={setRefs}
          className={className}
          onKeyDown={handleKeyDown}
          onClear={handleClear}
          {...props}
        />
      </div>
    )
  },
)

DropdownMenuSearch.displayName = "DropdownMenuSearch"

/* ── Sub-menu ──────────────────────────────────────────────── */

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuSubTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      dropdownMenuItemVariants(),
      "gap-2 text-coval-text",
      "focus:bg-surface-contrast data-[state=open]:bg-surface-contrast",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto size-3.5 text-coval-text-muted" />
  </DropdownMenuPrimitive.SubTrigger>
))

DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(dropdownMenuContentVariants(), className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))

DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

/* ── Exports ───────────────────────────────────────────────── */

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSearch,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  dropdownMenuContentVariants,
  dropdownMenuItemVariants,
}
